"use client";

import { SingleProduct } from "@/__generated__/graphql";
import { Box, Button, Stack, Typography, useScrollTrigger } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ProductMidSectionDescription from "./ProductMidSectionDescription";
import ProductMidSectionSpecifications from "./ProductMidSectionSpecifications";
import { useBreakPoints } from "@/lib/useBreakPoints";

type Props = {
  product: SingleProduct;
};

export default function ProductMidSection({ product }: Props) {
  if (!product.description || !(product.specifications && product.specifications.length)) {
    return <></>;
  }
  const trigger = useScrollTrigger({});
  const { smAndDown } = useBreakPoints();

  const [activeTab, setActiveTab] = useState<number>(0); // State for the active tab
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]); // Array of references for sections

  // Function to handle tab change
  const handleTabClick = (index: number): void => {
    setActiveTab(index);
    const el = sectionsRef.current[index];
    if (el) {
      const header = document.querySelector("header");
      const headerHeight = header ? header.offsetHeight : 0; // Default to 0 if header is not found
      // console.log({headerHeight}); // 57 73 80 152

      // Access the header element and get its height
      const elementPosition = el.getBoundingClientRect().top;

      const margin = +(smAndDown ? 40 : 47);
      const res = elementPosition;
      const shouldGoUp = res < 1;
      const shouldGoDown = res > 1;
      // console.log({ res, shouldGoUp, shouldGoDown });

      let t = shouldGoUp ? (smAndDown ? 73 : 152) : shouldGoDown ? (smAndDown ? 57 : 80) : 0;
      t += margin;

      const offset = t; // Adjust this value based on your fixed tab/header height

      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Function to handle intersection observer updates
  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const index = sectionsRef.current.indexOf(entry.target as HTMLDivElement);
        setActiveTab(index);
      }
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.4, // Trigger when 40% of the section is in view
    });
    // Observe all sections
    sectionsRef.current.forEach((section) => section && observer.observe(section));

    return () => {
      // Clean up the observer on unmount
      observer.disconnect();
    };
  }, []);

  const list = useMemo(() => {
    const l = [];

    if (product.description) {
      l.push({
        name: "description",
        title: "توضیحات",
        content: <ProductMidSectionDescription description={product.description} />,
      });
    }

    if (product.specifications && product.specifications.length > 0) {
      l.push({
        name: "specifications",
        title: "مشخصات",

        content: <ProductMidSectionSpecifications specifications={product.specifications} />,
      });
    }

    return l;
  }, [product.description, product.specifications]);

  return (
    <>
      <Stack
        sx={(t) => ({
          background: t.palette.background.default,
          position: "sticky",
          borderRadius: 0,
          transition: "0.2s all",
          top: trigger ? { xs: 64, sm:72, md: 79 } : { xs: 64, sm: 72, md: 138 },
          mb: 0.5,
          borderBottom: `1px solid ${t.palette.grey["100"]}`,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
        })}
      >
        {list.map((item, index) => {
          const isActive = activeTab === index;
          return (
            <Button
              variant="text"
              color={isActive ? "secondary" : "inherit"}
              size="large"
              key={index}
              onClick={() => handleTabClick(index)}
              sx={(t) => {
                return {
                  borderRadius: 0,
                  borderBottom: `2px solid ${isActive ? t.palette.secondary.main : "transparent"}`,
                };
              }}
            >
              <Typography variant="subtitle1" component={"span"}>
                {item.title}
              </Typography>
            </Button>
          );
        })}
      </Stack>

      <Stack spacing={2}>
        {list.map((item, index) => {
          return (
            <div key={index} id={item.name} ref={(el) => (sectionsRef.current[index] = el)}>
              {item.content}
            </div>
          );
        })}
      </Stack>
    </>
  );
}
